<template>
  <ValidationObserver v-slot="{ handleSubmit }">
    <b-modal
      v-model="modal.show"
      :title="isEdit ? `Edit Starting Point` : `Add New Starting Point`"
      :ok-title="`Submit`"
      :ok-disabled="isDisable"
      @ok.prevent="handleSubmit(onSubmit)"
      @hidden="onHideModal"
      @show="onShowModal"
    >
      <content-spinner :show="modal.loading" />
      <show-error
        :show="showError && !modal.loading"
        :text="modalError"
      />
      <b-form
        v-if="!modal.loading"
        @submit.prevent="handleSubmit(onSubmit)"
        @keydown.enter.prevent="handleSubmit(onSubmit)"
      >
        <input-widget
          v-for="field in initialFields"
          :ref="`${field.name}`"
          :model="form"
          :field="field"
          :autofocus="field.autofocus"
          @click="onInputClick"
        />
        <div v-for="point in endpoints">
          <div
            class="header-toggle m-1"
            @click="toggle(point)"
          >
            <b>{{ point.name }}</b>
            <font-awesome-icon
              v-if="point.collapse"
              class="float-right"
              :icon="['fas','chevron-down']"
            />
            <font-awesome-icon
              v-else
              class="float-right "
              :icon="['fas','chevron-up']"
            />
          </div>
          <b-collapse :id="point.value">
            <b-form-checkbox-group
              v-model="selectedCheckbox"
              class="mb-3"
              stacked
              :options="getCheckboxOptions(point.value)"
            />
          </b-collapse>
        </div>
      </b-form>
    </b-modal>
    <DataManagementModal
      :is-starting-point="true"
      :selected-field="selectedField"
      @on-submit="onColumnChoose"
    />
  </ValidationObserver>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '../../../core/components/ContentSpinner';
import InputWidget from '../../../core/components/complex-table/components/InputWidget';
import {v4 as uuidv4} from 'uuid';
import ShowError from '../../../core/components/ShowError';
import DataManagementModal from '../../../core/components/complex-table/DataManagementModal';

const {mapState, mapActions} = createNamespacedHelpers('connectorScreen');
const {
  mapState: mapDataManagementState,
  mapActions: mapDataManagementActions,
} = createNamespacedHelpers('dataManagement');
export default {
  name: 'StartingPointModal',
  components: {DataManagementModal, ShowError, InputWidget, ContentSpinner},
  data() {
    return {
      endpoints: [],
      selectedCheckbox: [],
      selectedField: {},
      showError: false,
      isDisable: false,
      modalError: '',
      form: {
        entityName: null,
        entityFieldId: null,
        entityField: '',
        apIsToFetch: [],
      },
      fields: {
        entityName: {
          type: 'select',
          required: true,
          label: 'Entity Name',
          name: 'entityName',
          data: {
            options: [],
          },
        },
        entityField: {
          type: 'text',
          required: true,
          label: 'Entity Field',
          placeholder: 'Select Entity Field',
          name: 'entityField',
        },
      },
    };
  },
  computed: {
    ...mapState({
      modal: state => state.startingPoint.modal,
      connectors: state => state.connectors,
      pitchBookEndpoints: state => state.startingPoint.pitchBookEndpoints,
      entityNameEndpoints: state => state.startingPoint.entityNameEndpoints,
    }),
    ...mapDataManagementState({
      allField: state => state.fields,
    }),
    isEdit() {
      return this.modal.data && Object.keys(this.modal.data).length > 2;
    },
    initialFields() {
      const fields = {...this.fields};
      fields.entityName.data.options = [];

      for (const index in this.pitchBookEndpoints.EntityNames) {
        if (this.pitchBookEndpoints.EntityNames.hasOwnProperty(index)) {
          fields.entityName.data.options.push({value: index, text: this.pitchBookEndpoints.EntityNames[index]});
        }
      }
      fields.entityName.data.options.unshift({value: null, text: 'Select Entity Name', disabled: true});

      return fields;
    },
  },
  methods: {
    ...mapActions(['hideStartingPointModal', 'saveStartingPoint']),
    ...mapDataManagementActions(['showDataManagementModal']),
    async onSubmit() {
      this.isDisable = true;
      const params = {...this.form};
      let action = 'update';

      if (!params.configurationId) {
        params.configurationId = this.modal.data.configurationId;
      }
      if (!params.startingPointId) {
        params.startingPointId = uuidv4();
        action = 'create';
      }

      // Add checked points
      this.selectedCheckbox.forEach(item => {
        const index = params.apIsToFetch.findIndex(e => e.apiEnumValue === item);
        if (index === -1) {
          params.apIsToFetch.push({
            apiToFetchId: uuidv4(),
            startingPointId: params.startingPointId,
            apiEnumValue: item,
          });
        }
      });
      // Delete unchecked points
      params.apIsToFetch.forEach(item => {
        const index = this.selectedCheckbox.findIndex(c => c === item.apiEnumValue);
        if (index === -1) {
          params.apIsToFetch = params.apIsToFetch.filter(a => a.apiEnumValue !== item.apiEnumValue);
        }
      });

      const connector = this.connectors.find(c => c.configurationId === this.$route.params.configurationId);
      let entityNameAlreadyExist = false;
      if (action === 'create' && connector) {
        for (const entity in connector.startingPoints) {
          if (connector.startingPoints.hasOwnProperty(entity) && connector.startingPoints[entity].entityName === params.entityName) {
            entityNameAlreadyExist = true;
          }
        }
      }
      if (!entityNameAlreadyExist) {
        const {success, body} = await this.saveStartingPoint({data: params, action});
        if (success) {
          this.$toasted.success(`Starting Point ${action}d successfully`, {
            duration: TOASTER_DURATION,
          });
          this.onHideModal();
        } else {
          this.isDisable = false;
          this.showError = true;
          this.modalError = body.ErrorMessage;
        }
      } else {
        this.isDisable = false;
        this.showError = true;
        this.modalError = `Entity Name "${this.getEntityName(params)}" already exist`;
      }
    },
    onHideModal() {
      this.form = {
        entityName: null,
        entityFieldId: null,
        entityField: '',
        apIsToFetch: [],
      };
      this.selectedField = {};
      this.isDisable = false;
      this.showError = false;
      this.modalError = '';
      this.selectedCheckbox = [];
      this.endpoints = [];
      this.hideStartingPointModal();
    },
    onShowModal() {
      if (this.isEdit) {
        this.form = {...this.modal.data};
        this.selectedField = this.allField.data.find(f => f.fieldGuid === this.form.entityFieldId);
        this.form.entityField = this.selectedField.displayName;

        if (this.modal.data.apIsToFetch.length) {
          this.selectedCheckbox = this.modal.data.apIsToFetch.map(item => item.apiEnumValue);
        }
      }
      this.endpoints = JSON.parse(JSON.stringify(this.entityNameEndpoints));
    },
    onInputClick(ev) {
      this.showDataManagementModal();
      ev.target.blur();
    },
    onColumnChoose(item) {
      this.form.entityFieldId = item.fieldGuid;
      this.form.entityField = item.displayName;
      this.form = {...this.form};

      this.selectedField = this.allField.data.find(f => f.fieldGuid === item.fieldGuid);
    },
    toggle(point) {
      point.collapse = !point.collapse;
      this.$root.$emit('bv::toggle::collapse', point.value);
    },
    getCheckboxOptions(point) {
      const options = [];
      for (const index in this.pitchBookEndpoints[point]) {
        if (this.pitchBookEndpoints[point].hasOwnProperty(index)) {
          options.push({value: index, text: this.pitchBookEndpoints[point][index]});
        }
      }
      return options;
    },
    getEntityName(item) {
      return this.pitchBookEndpoints.EntityNames[item.entityName] || item.entityName;
    },
  },
};
</script>

<style lang="scss" scoped>
.header-toggle {
  cursor: pointer;
}

</style>